import React, {Component, ReactNode} from 'react'
import {createResidentPage, ResidentPagePropsSet} from '../shared/hoc/createResidentPage'
import {MeetingRepositoryForResident} from './MeetingRepositoryForResident'
import NatCurrentDateTimeRepo
    from '../../__NoAutomatedTests/components/currentDateTime/repository/NatCurrentDateTimeRepo'
import EventEmitterExResident from '../shared/pageBaseComponents/EventEmitterExResident'
import MeetingSimple from './model/MeetingSimple'
import {MeetingCommentOutline} from './model/MeetingCommentOutline'
import {Nullable} from '../../app/TypeHelpers'
import moment, {Moment} from 'moment'
import {AVAILABLE_OR_NOT, ROLE_FOR_ADMINISTRATOR} from '../../CodeMaster'
import {judgeAuthorityCheck, NatAsyncCallFunction} from '../../__NoAutomatedTests/common/util/Natutil'
import {HKConstants} from '../../HKConstants'
import {
    BlueInfoIcon,
    IconBox,
    MeetingAttendanceDeadlineTag,
    MeetingIcon,
    MeetingIconBack,
    MiddleFontSpan,
    NewEllipsisText,
    PreWrap,
} from './AssocDirectorCommon'
import {
    NatGetCurrentDateTime,
} from '../../__NoAutomatedTests/components/currentDateTime/repository/NatGetCurrentDateTime'
import {ContentBody, MeetingTitle} from './AssocDirectorResidentCommon'
import {Box} from '@mui/material'
import {
    FullWidthColumnFlexContainer,
    FullWidthRowFlexContainer,
    LeftRowFlexContainer,
    LeftTopRowFlexContainer,
    RightRowFlexContainer,
    RowFlexContainer,
} from '../../styledComponents/FlexComponents'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faClock} from '@fortawesome/free-regular-svg-icons'
import {faMapMarkerAlt} from '@fortawesome/free-solid-svg-icons'
import {currentMedia, HideInSP} from '../../styledComponents/ResponsiveConvenience'
import {PlainForwardLinkStyledButton, PlainLinkStyledButton, SecondaryButton} from '../../styledComponents/Button'
import {StyledCard, StyledPageTitle, ViewOnlyModeMessage} from '../reservations/components/CommonStyle'
import {DisplayItemDividerForPageTop, DisplayItemTitle} from '../../styledComponents/DisplayItem'
import ResponsiveDialog from '../shared/ResponsiveDialog'
import ResidentRegisterMeetingAttendanceDialog from './ResidentRegisterMeetingAttendanceDialog'
import styled from 'styled-components'
import {Colors, FontSize, FontWeights} from '../../styledComponents/Constants'
import Scroll from 'react-scroll'
import {MeetingHelper} from './utils/MeetingHelper'
import BaseMeetingAttendanceResponse from './model/BaseMeetingAttendanceResponse'
import MeetingAttendanceResponse from './model/MeetingAttendanceResponse'
import ResidentUpdateMeetingAttendanceDialog from './ResidentUpdateMeetingAttendanceDialog'
import {AssocDirectorHelper} from './utils/AssocDirectorHelper'
import {Location} from 'react-router-dom'

const ScrollElement = Scroll.Element
const scroller = Scroll.scroller

interface LocationState {
    scrollTargetCommentId?: number
}

interface Props {
    condominiumCd: string
    meetingRepoForResident: MeetingRepositoryForResident
    currentDateTimeRepo: NatCurrentDateTimeRepo
    eventEmitter: EventEmitterExResident
    location: Location<LocationState>
}

interface State {
    meetings: MeetingSimple[],
    recentComments: MeetingCommentOutline[]
    meetingsNeedsAttendanceReply: MeetingNeedsAttendanceReply[],
    attendanceReplyingMeeting: Nullable<MeetingNeedsAttendanceReply>,
    meetingAttendanceResponseMap: Map<number, MeetingAttendanceResponse>
    isAttendanceDialogOpen: boolean,
    currentDateTime: Moment,
    havePostCommentAuthority: boolean,
    isLoadCompleted: boolean
}

interface MeetingNeedsAttendanceReply {
    id: number
    name: string
    dateString: string
    placeString: string
    attendanceReplyDeadline: Moment
    proxyAttendanceAvailability: AVAILABLE_OR_NOT
}

class ResidentAssocTopPage extends Component<Props & ResidentPagePropsSet, State> {
    constructor(props: Readonly<Props & ResidentPagePropsSet>) {
        super(props)

        this.state = {
            meetings: [],
            recentComments: [],
            meetingsNeedsAttendanceReply: [],
            attendanceReplyingMeeting: null,
            meetingAttendanceResponseMap: new Map<number, MeetingAttendanceResponse>(),
            isAttendanceDialogOpen: false,
            currentDateTime: moment(),
            havePostCommentAuthority: false,
            isLoadCompleted: false,
        }
        this.handleCloseMeetingAttendanceDialog = this.handleCloseMeetingAttendanceDialog.bind(this)
    }

    async componentDidMount(): Promise<void> {
        const [
            meetings,
            recentComments,
            meetingsNeedsAttendanceReply,
            currentDateTime,
        ] = await Promise.all([
            this.getMeetings(),
            this.getRecentComments(),
            this.getMeetingsNeedsAttendanceReply(),
            this.getCurrentDateTime(),
        ])
        if (meetings === null) {
            return
        }
        if (recentComments === null) {
            return
        }
        if (meetingsNeedsAttendanceReply === null) {
            return
        }
        if (currentDateTime === null || currentDateTime.details === null) {
            return
        }

        const meetingAttendanceResponseMap = new Map<number, MeetingAttendanceResponse>()
        for (const meeting of meetingsNeedsAttendanceReply) {
            const attendance = await this.getAttendance(meeting.id)
            if (attendance instanceof MeetingAttendanceResponse) {
                meetingAttendanceResponseMap.set(meeting.id, attendance)
            }
        }

        this.setState({
                meetings: meetings,
                recentComments: recentComments,
                meetingsNeedsAttendanceReply: meetingsNeedsAttendanceReply,
                meetingAttendanceResponseMap: meetingAttendanceResponseMap,
                currentDateTime: moment(currentDateTime.details.currentDateTime),
                havePostCommentAuthority: judgeAuthorityCheck(HKConstants.CMN.AUTH.POST_COMMENT_ASSOC_DIRECTOR),
                isLoadCompleted: true,
            },
            () => {
                const targetId = this.props.location.state?.scrollTargetCommentId
                if (targetId) {
                    scroller.scrollTo(`comment-${targetId}`, {
                        delay: 100,
                        duration: 500,
                        smooth: true,
                    })
                }
            })
    }

    async getMeetings(): Promise<Nullable<MeetingSimple[]>> {
        const {meetingRepoForResident, condominiumCd, navigate} = this.props

        await this.props.eventEmitter.showLoading()
        let meetings: Nullable<MeetingSimple[]>
        try {
            meetings = await NatAsyncCallFunction(
                meetingRepoForResident.getMeetings.bind(
                    meetingRepoForResident,
                    condominiumCd,
                ), navigate)
        } finally {
            this.props.eventEmitter.closeLoading()
        }
        return meetings
    }

    async getRecentComments(): Promise<Nullable<MeetingCommentOutline[]>> {
        const {meetingRepoForResident, condominiumCd, navigate} = this.props

        await this.props.eventEmitter.showLoading()
        let recentComments: Nullable<MeetingCommentOutline[]>
        try {
            recentComments = await NatAsyncCallFunction(
                meetingRepoForResident.getRecentComments.bind(
                    meetingRepoForResident,
                    condominiumCd,
                ), navigate)
        } finally {
            this.props.eventEmitter.closeLoading()
        }

        if (recentComments === null) {
            return null
        }

        return recentComments
    }

    async getMeetingsNeedsAttendanceReply(): Promise<Nullable<MeetingNeedsAttendanceReply[]>> {
        const {meetingRepoForResident, condominiumCd, navigate} = this.props

        await this.props.eventEmitter.showLoading()
        let meetings: Nullable<MeetingSimple[]>
        try {
            meetings = await NatAsyncCallFunction(
                meetingRepoForResident.getMeetingsNeedsAttendanceReply.bind(
                    meetingRepoForResident,
                    condominiumCd,
                ), navigate)
        } finally {
            this.props.eventEmitter.closeLoading()
        }
        if (meetings === null) {
            return null
        }
        return meetings.map(meeting => {
            if (meeting.attendanceReplyDeadline === null
                || meeting.proxyAttendanceAvailability === null) {
                return null
            }
            return {
                id: meeting.id,
                name: meeting.name,
                dateString: MeetingHelper.getMeetingDateString(meeting),
                placeString: MeetingHelper.getMeetingPlaceString(meeting),
                attendanceReplyDeadline: meeting.attendanceReplyDeadline,
                proxyAttendanceAvailability: meeting.proxyAttendanceAvailability,
            }
        }).filter((meeting): meeting is MeetingNeedsAttendanceReply => meeting !== null)
    }

    async getCurrentDateTime(): Promise<Nullable<NatGetCurrentDateTime>> {
        const {currentDateTimeRepo, navigate} = this.props

        await this.props.eventEmitter.showLoading()
        let currentDateTime: Nullable<NatGetCurrentDateTime> = null
        try {
            currentDateTime = await NatAsyncCallFunction(
                currentDateTimeRepo.getCurrentDateTime.bind(currentDateTimeRepo),
                navigate,
            )
        } finally {
            this.props.eventEmitter.closeLoading()
        }
        return currentDateTime
    }

    async getAttendance(meetingId: number): Promise<Nullable<BaseMeetingAttendanceResponse | 'Observer'>> {
        const {meetingRepoForResident, condominiumCd, navigate} = this.props

        await this.props.eventEmitter.showLoading()
        let attendance: Nullable<BaseMeetingAttendanceResponse | 'Observer'>
        try {
            attendance = await NatAsyncCallFunction(
                meetingRepoForResident.getAttendance.bind(
                    meetingRepoForResident,
                    condominiumCd,
                    meetingId,
                ), navigate)
        } finally {
            this.props.eventEmitter.closeLoading()
        }
        return attendance
    }

    handleDisplayMeetingDetailButtonClick = (selectedMeetingId: number) => (): void => {
        const {condominiumCd, navigate} = this.props

        navigate(
            `/resident/condominiums/${condominiumCd}/meetings/${selectedMeetingId}`,
            {state: {previousPageUrl: `/resident/condominiums/${condominiumCd}/assoc-top`}},
        )
    }

    handleClickMeetingAttendanceReplyButton = (
        meetingNeedsAttendanceReply: MeetingNeedsAttendanceReply,
    ) => (): void => {
        this.setState({
            attendanceReplyingMeeting: meetingNeedsAttendanceReply,
            isAttendanceDialogOpen: true,
        })
    }

    handleClickDisplayMeetingListButton = () => (): void => {
        const {condominiumCd, navigate} = this.props

        navigate(`/resident/condominiums/${condominiumCd}/meetings`)
    }

    async handleCloseMeetingAttendanceDialog(isOk: boolean): Promise<void> {
        if (isOk) {
            const meetings = await this.getMeetingsNeedsAttendanceReply()
            if (meetings !== null) {
                this.setState({
                    meetingsNeedsAttendanceReply: meetings,
                })
            }
        }
        this.setState({
            isAttendanceDialogOpen: false,
        })
    }

    handleClickRecentMeetingComment = (meetingId: number, meetingCommentId: number) => (): void => {
        const {condominiumCd, navigate} = this.props

        navigate(
            `/resident/condominiums/${condominiumCd}/meetings/${meetingId}/comments/${meetingCommentId}/reply`,
            {state: {previousPageUrlStack: [`/resident/condominiums/${condominiumCd}/assoc-top`]}},
        )
    }

    renderMeetingsNeedsAttendanceReply(): ReactNode {
        const {meetingsNeedsAttendanceReply} = this.state
        if (meetingsNeedsAttendanceReply.length === 0) {
            return
        }

        return (
            <ContentBody>
                <WhiteArea>
                    <Box height={'15px'}/>
                    <LeftRowFlexContainer>
                        <BlueInfoIcon/>
                        <Box width={'10px'}/>
                        <MiddleFontSpan>出欠を回答してください</MiddleFontSpan>
                    </LeftRowFlexContainer>
                    <Box height={'15px'}/>
                    {
                        meetingsNeedsAttendanceReply.map(meeting =>
                            <div key={meeting.id}>
                                <RowFlexContainer>
                                    <MeetingAttendanceDeadlineTag>
                                        {meeting.attendanceReplyDeadline.format('M/D')}まで
                                    </MeetingAttendanceDeadlineTag>
                                    <MeetingAttendanceTitle>
                                        {meeting.name}
                                    </MeetingAttendanceTitle>
                                    <MeetingAttendanceReplyButton
                                        onClick={this.handleClickMeetingAttendanceReplyButton(meeting)}
                                    >
                                        回答する
                                    </MeetingAttendanceReplyButton>
                                </RowFlexContainer>
                                <Box height={'15px'}/>
                            </div>,
                        )
                    }
                    <Box height={'5px'}/>
                </WhiteArea>
                <Box height={'15px'}/>
            </ContentBody>
        )
    }

    renderNextMeeting(): ReactNode {
        const {meetings, currentDateTime} = this.state

        const nextMeeting = MeetingHelper.getNextMeeting(meetings, currentDateTime)

        const adjustMeeting = meetings.filter(meeting => {
            return meeting.date === null
        })

        if (nextMeeting) {
            return (
                <Box width={'100%'} id={'NextMeeting'}>
                    <NextMeetingTagArea>
                        <Box height={'10px'}/>
                        <NextMeetingTag>次の会議</NextMeetingTag>
                        <Box height={'15px'}/>
                    </NextMeetingTagArea>
                    <WhiteArea>
                        <LeftRowFlexContainer>
                            <MeetingIconBack><MeetingIcon/></MeetingIconBack>
                            <Box minWidth={'10px'}/>
                            <MeetingTitle>
                                {nextMeeting.name}
                            </MeetingTitle>
                        </LeftRowFlexContainer>
                        <Box height={'10px'}/>
                        <LeftRowFlexContainer>
                            <IconBox><FontAwesomeIcon icon={faClock}/></IconBox>
                            {MeetingHelper.getMeetingDateString(nextMeeting)}
                        </LeftRowFlexContainer>
                        <Box height={'5px'}/>
                        <MiddleFontSpan>
                            <LeftTopRowFlexContainer>
                                <IconBox><FontAwesomeIcon icon={faMapMarkerAlt}/></IconBox>
                                {MeetingHelper.getMeetingPlaceString(nextMeeting)}
                            </LeftTopRowFlexContainer>
                        </MiddleFontSpan>
                        <Box height={'25px'}/>
                        <SecondaryButton
                            onClick={this.handleDisplayMeetingDetailButtonClick(nextMeeting.id)}
                        >
                            詳細を確認する
                        </SecondaryButton>
                        <Box height={'15px'}/>
                        <Box height={'15px'}/>
                    </WhiteArea>
                    <Box height={'15px'}/>
                </Box>
            )
        } else {
            return (<>
                <Headline>次の会議</Headline>
                <Box margin={'10px'}>
                    <MiddleFontSpan>
                        {adjustMeeting.length > 0 ? '次の会議は調整中です' : '次の会議はありません'}
                    </MiddleFontSpan>
                </Box>
                <Box height={'15px'}/>
            </>)
        }
    }

    renderRecentComments(): ReactNode {
        const {recentComments} = this.state

        if (recentComments.length === 0) {
            return <FullWidthColumnFlexContainer>
                <Box mt={'20px'} mb={'10px'}>最近のコメントはありません</Box>
            </FullWidthColumnFlexContainer>
        }

        return <MeetingCardList>
            <Box height={'5px'}/>
            {
                recentComments.map((comment, index) => {
                    const {
                        meetingId,
                        meetingCommentId,
                        replySourceId,
                        replySourceContent,
                        meetingName,
                        content,
                        familyName,
                        firstName,
                        roleId,
                        roomList,
                        isWithinTerm,
                        hasMailAddress,
                        registerDatetime,
                    } = comment

                    const scrollTargetCommentId = replySourceId ? replySourceId : meetingCommentId
                    return <StyledScrollElement key={index} name={`comment-${scrollTargetCommentId}`}>
                        <MeetingCard onClick={this.handleClickRecentMeetingComment(
                            meetingId,
                            replySourceId ? replySourceId : meetingCommentId,
                        )}>
                            <MeetingCardTitleArea>
                                <RecentCommentTitleSpan>
                                    <div>{meetingName}</div>
                                    {
                                        replySourceContent && <NoWrapText>
                                            <LeftRowFlexContainer>
                                                「 <QuoteText>{replySourceContent}</QuoteText> 」への返信
                                                <HideInSP><Box mr={'180px'}/></HideInSP>
                                            </LeftRowFlexContainer>
                                        </NoWrapText>
                                    }
                                </RecentCommentTitleSpan>
                            </MeetingCardTitleArea>
                            <MeetingCardBodyArea>
                                <FullWidthRowFlexContainer>
                                    {MeetingHelper.getUserIconNodeByResidentStatus(
                                        isWithinTerm,
                                        hasMailAddress,
                                        ROLE_FOR_ADMINISTRATOR.codeToEnumConstants(roleId),
                                        roomList,
                                        'small',
                                    )}
                                    <UserInformationArea>
                                        <RecentCommentUserNameSpan>
                                            {AssocDirectorHelper.getDisplayUserNameByResidentStatus(
                                                familyName, firstName, roomList,
                                            )}
                                        </RecentCommentUserNameSpan>
                                        <Box width={8}/>
                                        <RecentCommentDatetimeSpan>
                                            {MeetingHelper.formatDoNotDisplayCurrentYear(
                                                this.state.currentDateTime.format('YYYY-MM-DDTHH:mm:ss'),
                                                registerDatetime)}
                                        </RecentCommentDatetimeSpan>
                                    </UserInformationArea>
                                </FullWidthRowFlexContainer>
                                <Box height={'5px'}/>
                                <NewEllipsisText lineHeightInPixel={24} numOfLines={2}>
                                    <PreWrap>
                                        {content}
                                    </PreWrap>
                                </NewEllipsisText>
                            </MeetingCardBodyArea>
                        </MeetingCard>
                    </StyledScrollElement>
                })
            }</MeetingCardList>
    }

    renderMeetingAttendanceDialog(attendanceReplyingMeeting: MeetingNeedsAttendanceReply): ReactNode {
        const {
            condominiumCd,
            meetingRepoForResident,
            navigate,
            eventEmitter,
        } = this.props
        const {
            meetingAttendanceResponseMap,
            isAttendanceDialogOpen,
        } = this.state

        const meetingAttendanceResponse = meetingAttendanceResponseMap.get(attendanceReplyingMeeting.id)

        return <ResponsiveDialog
            id={'pp-dialog'}
            open={isAttendanceDialogOpen}
            transitionDuration={(process.env.NODE_ENV === 'test') ? 0 : undefined}
            maxWidth={'sm'}
            fullWidth
        >
            {meetingAttendanceResponse ?
                <ResidentUpdateMeetingAttendanceDialog
                    condominiumCd={condominiumCd}
                    meetingId={attendanceReplyingMeeting.id}
                    meetingName={attendanceReplyingMeeting.name}
                    meetingDateString={attendanceReplyingMeeting.dateString}
                    meetingPlaceString={attendanceReplyingMeeting.placeString}
                    proxyAttendanceAvailability={attendanceReplyingMeeting.proxyAttendanceAvailability}
                    meetingAttendance={meetingAttendanceResponse}
                    readOnly={false}
                    meetingRepo={meetingRepoForResident}
                    eventEmitter={eventEmitter}
                    navigate={navigate}
                    onClose={this.handleCloseMeetingAttendanceDialog}
                />
                :
                < ResidentRegisterMeetingAttendanceDialog
                    condominiumCd={condominiumCd}
                    meetingId={attendanceReplyingMeeting.id}
                    meetingName={attendanceReplyingMeeting.name}
                    meetingDateString={attendanceReplyingMeeting.dateString}
                    meetingPlaceString={attendanceReplyingMeeting.placeString}
                    proxyAttendanceAvailability={attendanceReplyingMeeting.proxyAttendanceAvailability}
                    meetingRepo={meetingRepoForResident}
                    eventEmitter={eventEmitter}
                    navigate={navigate}
                    onClose={this.handleCloseMeetingAttendanceDialog}
                />
            }
        </ResponsiveDialog>
    }

    render(): ReactNode {
        if (!this.state.isLoadCompleted) {
            return null
        }

        const {
            attendanceReplyingMeeting,
            havePostCommentAuthority,
        } = this.state

        return (<>
            <StyledPageTitle>理事会</StyledPageTitle>
            {
                !havePostCommentAuthority &&
                <ContentBody>
                    <Box height={'5px'}/>
                    <ViewOnlyModeMessage>会議情報を確認できます。コメントの投稿/返信はできません。</ViewOnlyModeMessage>
                    <Box height={'15px'}/>
                </ContentBody>
            }
            {
                havePostCommentAuthority &&
                <Box height={'15px'}/>
            }
            {this.renderMeetingsNeedsAttendanceReply()}
            <ContentBody>
                {this.renderNextMeeting()}
            </ContentBody>
            <RightRowFlexContainer>
                <PlainForwardLinkStyledButton
                    onClick={this.handleClickDisplayMeetingListButton()}
                >
                    すべての会議を見る
                </PlainForwardLinkStyledButton>
            </RightRowFlexContainer>
            <Box height={'20px'}/>
            <DisplayItemDividerForPageTop/>
            <Box height={'30px'}/>
            <DisplayItemTitle>最近のコメント</DisplayItemTitle>
            <ContentBody>
                {this.renderRecentComments()}
            </ContentBody>

            <Box height={'15px'}/>
            {attendanceReplyingMeeting && this.renderMeetingAttendanceDialog(attendanceReplyingMeeting)}
        </>)
    }
}

export default createResidentPage(ResidentAssocTopPage)
const WhiteArea = styled.div`
  background-color: ${Colors.Surface};
  ${currentMedia.lessThan('sp')`
    width: calc(100% + 30px);
    margin: 0px -15px;
  `};
  padding: 0px 15px;
  box-sizing: border-box;
`
const NextMeetingTagArea = styled.div`
  background-color: ${Colors.Surface};
  ${currentMedia.lessThan('sp')`
    width: calc(100% + 30px);
    margin: 0px -15px;
  `};
  padding: 0px 0px;
  box-sizing: border-box;
`
const Headline = styled.div`
  font-size: ${FontSize.Accent};
  font-weight: ${FontWeights.Bold};
  color: ${Colors.AccentDark};
`

const MeetingAttendanceTitle = styled.div`
  flex-grow: 1;
  font-size: ${FontSize.Middle};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin: 0px 5px;
`

const MeetingAttendanceReplyButton = styled(PlainLinkStyledButton)`
  flex-shrink: 0;
  font-size: ${FontSize.Middle};
`

const MeetingCardList = styled.div`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  align-items: center;
  width: 100%;
`

const MeetingCard = styled(StyledCard)`
  display: flex;
  box-sizing: border-box;
  flex-direction: column;
  justify-content: left;
  width: 100%;
  box-shadow: 0px 1px 8px rgba(112, 112, 112, 0.4);
  margin-top: 15px;
  ${currentMedia.lessThan('sp')`
    max-width: 384px;
  `}
  ${currentMedia.greaterThan('pc')`
    align-items: stretch;
  `}
`

const NoWrapText = styled.span`
  white-space: nowrap;
`

const StyledScrollElement = styled(ScrollElement)`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 670px;
`

const MeetingCardTitleArea = styled.div`
  background-color: rgb(247, 247, 247);
  width: 100%;
  padding: 5px 10px;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  box-sizing: border-box;
`

const MeetingCardBodyArea = styled.div`
  padding: 15px;
  width: 100%;
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
  box-sizing: border-box;
`

const UserInformationArea = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-left: 5px;
`

const RecentCommentTitleSpan = styled.span`
  font-size: 12px;
  color: rgba(112, 112, 112, 1);
`

const RecentCommentUserNameSpan = styled.span`
  font-size: 14px;
`

const RecentCommentDatetimeSpan = styled.span`
  font-size: 12px;
  white-space: nowrap;
`

const NextMeetingTag = styled.div`
  display: inline-block;
  position: relative;
  height: 24px;
  line-height: 24px;
  text-align: center;
  padding: 0 23px 0 15px;
  font-size: 16px;
  background: #0070B0;
  color: #FFF;
  box-sizing: border-box;

  &::after {
    position: absolute;
    content: '';
    width: 0px;
    height: 0px;
    z-index: 1;
  }

  &::after {
    top: 0px;
    right: -0.1px;
    border-width: 12px 6px 12px 0px;
    border-color: transparent #fff transparent transparent;
    border-style: solid;
  }
`

const QuoteText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
`
